import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftRounded, ChevronRightRounded, SendRounded } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    IconButton,
    Radio,
    Stack,
    TextField,
    Typography,
    alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { HarnessAwaitingData, useAndieChatControls } from '@/providers/andie-chat-provider';

/** Sentinel value identifying the always-present "write your own answer" free-text choice. */
const WRITE_OWN = '__write_own__';

/** Per-question answer state: selected option keys and the free-text value. */
type AnswerState = {
    selected: string[];
    ownText: string;
};

const EMPTY_ANSWER: AnswerState = { selected: [], ownText: '' };

/** True when the question has at least one prewritten option selected or non-empty free text. */
const isAnswered = (answer: AnswerState): boolean => {
    const optionsSelected = answer.selected.filter((key) => key !== WRITE_OWN);
    const ownFilled = answer.selected.includes(WRITE_OWN) && answer.ownText.trim().length > 0;
    return optionsSelected.length > 0 || ownFilled;
};

/** A predefined answer option: a short subject and an optional longer description. */
type AskUserOption = { subject: string; description?: string };

/**
 * The chosen answers (subjects) for a single question, in option order (not selection order) for
 * stability: the selected prewritten option subjects plus the free-text value when "write your own"
 * is used.
 */
const selectedAnswers = (options: AskUserOption[], answer: AnswerState): string[] => {
    const parts: string[] = [];
    options.forEach((option, index) => {
        if (answer.selected.includes(String(index))) parts.push(option.subject);
    });
    if (answer.selected.includes(WRITE_OWN) && answer.ownText.trim().length > 0) {
        parts.push(answer.ownText.trim());
    }
    return parts;
};

/**
 * Interactive answer UI for a 'needs-input' harness pause that carries prewritten `userQuestions`
 * (1-4 questions). Shows ONE question at a time (a wizard): its options as radios (single-select) or
 * checkboxes (multi-select), plus an always-present "write your own" free-text choice. The header shows
 * the question's header and, when there is more than one question, a `current/total` counter; prev/next
 * arrows navigate between questions. The primary button reads "Next" until the last question, where it
 * becomes "Send". On send it wraps the answers in a `<poll-answers>` payload (the chat renders that as
 * an answers card, and the harness feeds the JSON back into the paused step) and resumes the paused run
 * via `sendMessage`. The component unmounts once the run resumes, because sending clears `harnessAwaiting`.
 */
export const AndieAskUserPrompt = memo(({ awaiting }: { awaiting: HarnessAwaitingData }) => {
    const { t } = useTranslation();
    const { sendMessage, isLoading, isHydrated } = useAndieChatControls();

    const questions = useMemo(() => awaiting.userQuestions ?? [], [awaiting.userQuestions]);

    // One AnswerState per question (keyed by index) plus the current wizard step.
    const [answers, setAnswers] = useState<AnswerState[]>(() => questions.map(() => EMPTY_ANSWER));
    const [step, setStep] = useState(0);

    const updateAnswer = useCallback((index: number, updater: (prev: AnswerState) => AnswerState) => {
        setAnswers((prev) => prev.map((answer, i) => (i === index ? updater(answer) : answer)));
    }, []);

    const toggle = useCallback(
        (index: number, key: string, allowMultiple: boolean) => {
            updateAnswer(index, (prev) => {
                if (allowMultiple) {
                    const selected = prev.selected.includes(key)
                        ? prev.selected.filter((k) => k !== key)
                        : [...prev.selected, key];
                    return { ...prev, selected };
                }
                // Single-select: picking a new choice replaces the previous one.
                return { ...prev, selected: prev.selected.includes(key) ? [] : [key] };
            });
        },
        [updateAnswer],
    );

    const setOwnText = useCallback(
        (index: number, value: string) => {
            updateAnswer(index, (prev) => ({ ...prev, ownText: value }));
        },
        [updateAnswer],
    );

    const total = questions.length;
    const isLast = step >= total - 1;
    const current = questions[step];
    const currentAnswer = answers[step] ?? EMPTY_ANSWER;
    const currentAnswered = isAnswered(currentAnswer);
    const allAnswered = answers.length === total && answers.every(isAnswered);
    const disabled = isLoading || !isHydrated;

    // The submitted message wraps the structured answers in a <poll-answers> payload: the chat detects
    // it and renders an answers card, while the harness feeds the JSON back into the paused step.
    const payload = useMemo(() => {
        const items = questions.map((q, index) => ({
            header: q.header,
            question: q.question,
            answers: selectedAnswers(q.options, answers[index] ?? EMPTY_ANSWER),
        }));
        return `<poll-answers>${JSON.stringify(items)}</poll-answers>`;
    }, [questions, answers]);

    const goPrev = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);
    const goNext = useCallback(() => setStep((s) => Math.min(total - 1, s + 1)), [total]);

    const handleSubmit = useCallback(() => {
        if (!allAnswered || disabled) return;
        sendMessage(payload);
    }, [allAnswered, disabled, payload, sendMessage]);

    if (!current) return null;

    const counterLabel = total > 1 ? `${step + 1}/${total}` : '';

    return (
        <Box
            sx={(theme) => ({
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.andie.main, 0.24)}`,
                backgroundColor: alpha(theme.palette.andie.main, 0.06),
            })}
        >
            {/* Header: question header + counter (when >1), and prev/next navigation arrows. */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                    {current.header ? (
                        <Chip
                            label={counterLabel ? `${current.header} · ${counterLabel}` : current.header}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    ) : (
                        counterLabel && (
                            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'text.secondary' }}>
                                {counterLabel}
                            </Typography>
                        )
                    )}
                </Stack>
                {total > 1 && (
                    <Stack direction="row" gap={0.25}>
                        <IconButton size="small" onClick={goPrev} disabled={step === 0} sx={{ p: 0.25 }}>
                            <ChevronLeftRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={goNext}
                            disabled={isLast || !currentAnswered}
                            sx={{ p: 0.25 }}
                        >
                            <ChevronRightRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>
                )}
            </Stack>

            <Typography
                sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 0.5,
                    whiteSpace: 'pre-wrap',
                }}
            >
                {current.question}
            </Typography>

            <Stack gap={0.25}>
                {current.options.map((option, optIndex) => {
                    const key = String(optIndex);
                    const checked = currentAnswer.selected.includes(key);
                    return (
                        <FormControlLabel
                            key={key}
                            checked={checked}
                            onChange={() => toggle(step, key, current.allowMultiple)}
                            control={
                                current.allowMultiple ? (
                                    <Checkbox size="small" sx={{ py: 0.25 }} />
                                ) : (
                                    <Radio size="small" sx={{ py: 0.25 }} />
                                )
                            }
                            label={
                                <Box>
                                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary' }}>
                                        {option.subject}
                                    </Typography>
                                    {option.description && (
                                        <Typography
                                            sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.125 }}
                                        >
                                            {option.description}
                                        </Typography>
                                    )}
                                </Box>
                            }
                            sx={{ alignItems: 'flex-start', m: 0, py: 0.25 }}
                        />
                    );
                })}

                {/* Always-present free-text "write your own answer" choice. */}
                <Box>
                    <FormControlLabel
                        checked={currentAnswer.selected.includes(WRITE_OWN)}
                        onChange={() => toggle(step, WRITE_OWN, current.allowMultiple)}
                        control={
                            current.allowMultiple ? (
                                <Checkbox size="small" sx={{ py: 0.25 }} />
                            ) : (
                                <Radio size="small" sx={{ py: 0.25 }} />
                            )
                        }
                        label={
                            <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary' }}>
                                {t('andie.chat.ask-user.write-your-own')}
                            </Typography>
                        }
                        sx={{ alignItems: 'flex-start', m: 0, py: 0.25 }}
                    />
                    <Collapse in={currentAnswer.selected.includes(WRITE_OWN)} unmountOnExit>
                        <Box sx={{ pl: 3.75, pb: 0.5 }}>
                            <TextField
                                value={currentAnswer.ownText}
                                onChange={(e) => setOwnText(step, e.target.value)}
                                placeholder={t('andie.chat.ask-user.write-your-own-placeholder')}
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={6}
                                slotProps={{ htmlInput: { 'aria-label': t('andie.chat.ask-user.write-your-own') } }}
                                sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
                            />
                        </Box>
                    </Collapse>
                </Box>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                {isLast ? (
                    <LoadingButton
                        variant="contained"
                        size="small"
                        loading={isLoading}
                        disabled={!allAnswered || disabled}
                        onClick={handleSubmit}
                        endIcon={<SendRounded sx={{ fontSize: 16 }} />}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                        {t('andie.chat.ask-user.send')}
                    </LoadingButton>
                ) : (
                    <Button
                        variant="contained"
                        size="small"
                        disabled={!currentAnswered || disabled}
                        onClick={goNext}
                        endIcon={<ChevronRightRounded sx={{ fontSize: 16 }} />}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                        {t('andie.chat.ask-user.next')}
                    </Button>
                )}
            </Stack>
        </Box>
    );
});

AndieAskUserPrompt.displayName = 'AndieAskUserPrompt';
